import React, { Component } from 'react';
import PropTypes from 'prop-types';
import noUiSlider from 'nouislider';
import { json } from 'd3';
import { geoConicConformal, geoPath } from 'd3-geo';
import { select } from 'd3-selection';
import './querybuilder.scss';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import parse from 'html-react-parser';
import * as Icon from 'react-feather';
import Datawrapper from '../datawrapper/datawrapper.jsx';
import colors from '../../../../../../../../apps/baykis-pl/pattern-lab/_data/generated_colors.json';
import config from './config.json';
import IconButton from '../iconButton/IconButton.jsx';
import {
  characteristics as querybuilderCharacteristics,
  timeCharacteristics,
} from './querybuilderCharacteristics.json';
import MapYearSelect, { defaultMapYears } from '../mapYearSelect/MapYearSelect';

const apiQuery = [];
const host = window.location.hostname;
let prefix = '/themes/baykis/dist/app-drupal';
let baseURL = window.location.origin;

if (
  host.includes('patternlab') ||
  host.includes('styleguide') ||
  host === 'dev.baykis.test' ||
  (host.includes('localhost') && !host.includes('drupal'))
) {
  prefix = '';
  baseURL = 'http://drupal_web';
}

if (host.includes('baykis.test')) {
  baseURL = 'http://drupal_web';
}

const shapeURL = `${prefix}/assets/statics/data/baykis_bayern_geo.json`;
const tasURL = `${prefix}/assets/statics/data/set_tas.json`;
const prURL = `${prefix}/assets/statics/data/set_pr.json`;
const agrarURL = `${prefix}/assets/statics/data/set_agrar.json`;
const healthURL = `${prefix}/assets/statics/data/set_health.json`;
const tourismURL = `${prefix}/assets/statics/data/set_tourism.json`;
const waterURL = `${prefix}/assets/statics/data/set_water.json`;
const natureURL = `${prefix}/assets/statics/data/set_nature.json`;
const forestURL = `${prefix}/assets/statics/data/set_forest.json`;
const soilURL = `${prefix}/assets/statics/data/set_soil.json`;
const catastropheURL = `${prefix}/assets/statics/data/set_catastrophe.json`;
const planningURL = `${prefix}/assets/statics/data/set_planning.json`;
const cityURL = `${prefix}/assets/statics/data/set_city.json`;
const architectureURL = `${prefix}/assets/statics/data/set_architecture.json`;
const trafficURL = `${prefix}/assets/statics/data/set_traffic.json`;
const energyURL = `${prefix}/assets/statics/data/set_energy.json`;
const industryURL = `${prefix}/assets/statics/data/set_industry.json`;
const financeURL = `${prefix}/assets/statics/data/set_finance.json`;
const filterSet = [];
let timeCharacteristic = timeCharacteristics[0];
let dynamicContext = null;

const selectbox = {
  minMax: [
    {
      label: 'Minimum',
      value: 'min',
    },
    {
      label: 'Median',
      value: 'median',
    },
    {
      label: 'Maximum',
      value: 'max',
    },
  ],
  rcp: [
    { label: 'RCP 2.6', value: 'rcp26' },
    { label: 'RCP 4.5', value: 'rcp45' },
    { label: 'RCP 8.5', value: 'rcp85' },
  ],
  rcpComparison: [
    { label: 'RCP 8.5 vs. RCP 2.6', value: 'rcp85VSrcp26' },
    { label: 'RCP 4.5 vs. RCP 2.6', value: 'rcp45VSrcp26' },
    { label: 'RCP 8.5 vs. RCP 4.5', value: 'rcp85VSrcp45' },
  ],
  modes: {
    future: [
      { label: 'Standard', value: 'standard' },
      { label: 'Szenarienvergleich', value: 'scenarios' },
      { label: 'Zeitvergleich', value: 'time' },
    ],
    past: [
      { label: 'Standard', value: 'standard' },
      { label: 'Warming Stripes', value: 'warmingstripes' },
      { label: 'Jahresvergleich', value: 'years' },
    ],
  },
  regions: [
    { label: 'Bayern', value: 'bavaria' },
    { label: 'Regierungsbezirke', value: 'govregion' },
    { label: 'Landkreise', value: 'county' },
    { label: 'Klimaregionen', value: 'climaregion' },
    { label: 'Naturräume', value: 'nature' },
  ],
  climatics: [
    { label: 'Temperatur', value: 'tas' },
    { label: 'Niederschlag', value: 'pr' },
    { label: 'Wasserwirtschaft', value: 'water' },
    { label: 'Landwirtschaft', value: 'agrar' },
    { label: 'Wald und Forstwirtschaft', value: 'forest' },
    { label: 'Naturschutz', value: 'nature' },
    { label: 'Bodenschutz und Georisiken', value: 'soil' },
    { label: 'Menschliche Gesundheit', value: 'health' },
    { label: 'Katastrophenschutz', value: 'catastrophe' },
    { label: 'Raumordnung', value: 'planning' },
    { label: 'Städtebau / Bauleitplanung', value: 'city' },
    { label: 'Bauwesen', value: 'architecture' },
    { label: 'Straßenbau und Verkehr', value: 'traffic' },
    { label: 'Energiewirtschaft', value: 'energy' },
    { label: 'Industrie und Gewerbe', value: 'industry' },
    { label: 'Tourismus', value: 'tourism' },
    { label: 'Finanzwirtschaft', value: 'finance' },
  ],
};

const searchParams = new URLSearchParams(window.location.search);
let activeRegionBuffer = parseInt(searchParams.get('region'), 10) || 11;

class QueryBuilder extends Component {
  bayernShape;

  geopath;

  constructor(props) {
    super(props);
    this.mode = React.createRef();
    this.regions = React.createRef();
    this.timeframe = React.createRef();
    this.timeframeBegin = React.createRef();
    this.climatics = React.createRef();
    this.characteristics = React.createRef();
    this.rcp = React.createRef();
    this.rcpComparison = React.createRef();
    this.minMaxMed = React.createRef();
    this.minimapSvgRef = React.createRef();
    this.slider = null;

    this.selectedCharacteristic = {
      label: '',
      unit: '',
    };

    const { epoch } = this.props;
    const yearsMapSelectionParam = searchParams.getAll('yearsMapSelection');
    let yearsMapSelection;
    if (yearsMapSelectionParam && yearsMapSelectionParam.length > 0) {
      yearsMapSelection = [];
      yearsMapSelectionParam.forEach((year) => {
        yearsMapSelection.push({ value: year, label: year });
      });
    } else {
      yearsMapSelection = defaultMapYears;
    }
    let selectedYear;
    if (searchParams.get('year')) {
      selectedYear = searchParams.get('year');
    } else if (epoch === 'past') {
      selectedYear = '2019';
    } else {
      selectedYear = '2085';
    }
    const timeCharacteristicParam = searchParams.get('timeCharacteristic');
    timeCharacteristic =
      timeCharacteristics.find(
        (element) => element.value === timeCharacteristicParam
      ) || timeCharacteristics[0];

    this.state = {
      context: searchParams.get('context') || props.context,
      vistype: searchParams.get('vistype') || props.vistype,
      apiParameter: [],
      epoch: searchParams.get('epoch') || epoch,
      mode: searchParams.get('mode') || 'standard',
      modeFinal: searchParams.get('mode') || 'standard',
      fullShape: {},
      filteredShape: [],
      activeRegion: activeRegionBuffer,
      regionName: searchParams.get('regionName') || 'Bayern',
      regionNameFinal: searchParams.get('regionName') || 'Bayern',
      regionPartitioning: searchParams.get('regionPartitioning') || 'Bayern',
      regionPartitioningValue: searchParams.get('regionPartValue') || 'bavaria',
      regionPartitioningValueFinal:
        searchParams.get('regionPartValue') || 'bavaria',
      regionPartitioningLabelFinal:
        searchParams.get('regionPartitioning') || 'Bayern',
      calledRCP: searchParams.get('rcp') || 'rcp85',
      calledComparisonRCP: searchParams.get('comp_rcp') || 'rcp85VSrcp26',
      yearsMapSelection,
      rcpFilter: searchParams.getAll('rcpFilter') || ['rcp85_graph'],
      selectedYear,
      defaultIsSet: false,
      characteristics: querybuilderCharacteristics,
      defaultContext: [
        // Array mit aufsteigender Zahlenfolge (0-39)
        [...Array(38).keys()].slice(0),
        // Array mit aufsteigender Zahlenfolge (0-28)
        [...Array(29).keys()].slice(0),
      ],
      filteredOptions: [],
      defaultClimatics: searchParams.get('climatics') || 'pr',
      defaultCharacteristics: searchParams.get('characteristics') || 'pr-Y',
      aggregation: searchParams.get('aggregation') || 'median',
      usp: searchParams.get('usp') || 'view',
    };
  }

  componentDidMount() {
    const {
      defaultContext,
      defaultIsSet,
      regionPartitioningValue,
      mode,
      vistype,
    } = this.state;
    this.initFilterSet(defaultContext);
    this.loadFilterSets();

    this.buildMinimap(regionPartitioningValue);
    if (!defaultIsSet) {
      const { context } = this.state;
      this.updateApi(context);
    }
    // this.useFilter(vistype); // set initial params to prevent unwanted behaviour
    const apiQueryNew = [...apiQuery];
    this.setState({
      apiParameter: apiQueryNew,
    });

    this.setChartTitle(mode);
  }

  componentDidUpdate() {
    const { defaultIsSet, epoch } = this.state;
    if (!defaultIsSet) {
      const { context } = this.state;
      this.updateApi(context);
    }

    this.buildSlider(this.timeframe.current, epoch);
  }

  loadFilterSets = () => {
    const promises = [];
    const files = [
      tasURL,
      prURL,
      agrarURL,
      healthURL,
      tourismURL,
      waterURL,
      natureURL,
      forestURL,
      soilURL,
      catastropheURL,
      planningURL,
      cityURL,
      architectureURL,
      trafficURL,
      energyURL,
      industryURL,
      financeURL,
    ];

    files.forEach((url) => {
      promises.push(
        fetch(url, {
          method: 'get',
        })
          .then((response) => response.json())
          .then((response) => response)
      );
    });
    Promise.all(promises).then((values) => {
      filterSet.push(values);
    });
  };

  updateApi = (context) => {
    const {
      mode,
      calledRCP,
      activeRegion,
      selectedYear,
      aggregation,
      yearsMapSelection,
    } = this.state;
    apiQuery[0] = '';
    apiQuery[1] = calledRCP;
    apiQuery[2] = aggregation;
    apiQuery[3] = selectedYear;
    apiQuery[4] = activeRegion;
    apiQuery[5] = yearsMapSelection;
    const { characteristics } = this.state;

    const characteristicsFlattened = characteristics.flat();
    if (context === 'niederschlag' || context === 'wasserwirtschaft') {
      apiQuery[0] = searchParams.get('characteristics') || 'pr-Y';
      const selectedCharacteristicTemp = characteristicsFlattened.find(
        (element) => {
          return element.value === apiQuery[0];
        }
      );
      this.selectedCharacteristic = selectedCharacteristicTemp;

      this.setState({
        defaultClimatics: searchParams.get('climatics') || 'pr',
        defaultCharacteristics: searchParams.get('characteristics') || 'pr-Y',
        chartTitle: characteristics[1][0].label,
        unit: characteristics[1][0].unit,
        defaultIsSet: true,
        apiParameter: apiQuery,
      });
    } else {
      apiQuery[0] = searchParams.get('characteristics') || 'tas-TM-Y';
      const selectedCharacteristicTemp = characteristicsFlattened.find(
        (element) => {
          return element.value === apiQuery[0];
        }
      );
      this.selectedCharacteristic = selectedCharacteristicTemp;

      let chartTitle = characteristics[0][0].label;
      if (mode === 'time') {
        chartTitle = timeCharacteristics[0].label;
      }

      this.setState({
        defaultClimatics: searchParams.get('climatics') || 'tas',
        defaultCharacteristics:
          searchParams.get('characteristics') || 'tas-TM-Y',
        chartTitle,
        unit: characteristics[0][0].unit,
        defaultIsSet: true,
        apiParameter: apiQuery,
      });
    }
  };

  changeMode = (event) => {
    this.setState({
      mode: event.currentTarget.value,
    });
  };

  applyMode = () => {
    const { mode, modeFinal, epoch } = this.state;

    if (
      mode !== modeFinal &&
      epoch === 'past' &&
      (modeFinal === 'warmingstripes' || modeFinal === 'years')
    ) {
      this.slider = null;
    }
    this.setChartTitle(mode);
    this.setState({
      modeFinal: mode,
    });
    if (mode === 'years') {
      this.setState({
        vistype: 'map',
      });
    }
  };

  changeRegion = (event) => {
    const regionPartitioningValue = event.currentTarget.value;
    const regionPartitioning = selectbox.regions.find((regionGroupObject) => {
      return regionGroupObject.value === regionPartitioningValue;
    });

    this.buildMinimap(regionPartitioningValue);

    this.setState({
      regionName: '',
      regionPartitioning: regionPartitioning.label,
      regionPartitioningValue: regionPartitioning.value,
    });
  };

  selectRegionOnMap = (activeNode, regionName) => {
    const selection = select('path.regionActive');
    const selectedNode = selection.node();

    selection.style('fill', colors.themeBase.box);
    selectedNode?.classList.remove('regionActive');

    if (activeNode && activeNode.getAttribute('dataname') === regionName) {
      const newSelection = select(activeNode);

      newSelection.attr('class', 'regionActive');
      newSelection.style('fill', colors.themeBase.accentDark);
    }
  };

  buildMinimap = (region) => {
    // GeoJSON Features Indizes von bis der jeweiligen Region
    const regionConfig = {
      bavaria: { idFrom: 11, idTo: 11 },
      govregion: { idFrom: 12, idTo: 18 },
      county: { idFrom: 19, idTo: 47 },
      climaregion: { idFrom: 119, idTo: 125 },
      nature: { idFrom: 1, idTo: 4 },
      default: { idFrom: 11, idTo: 11 },
    };
    const { idFrom, idTo } = regionConfig[region];
    const svgWidth = 273;
    const svgHeight = 300;
    const svgPaddingTop = 15;
    const svgPaddingBottom = 25;

    // Je nach ausgewählter Region werden hier nur die zu betrachtenden Features hinein gefügt
    let filteredShape;
    const filterShapes = () => {
      const filtered = this.bayernShape.features.filter(
        (feature) =>
          feature.properties.OBJECTID >= idFrom &&
          feature.properties.OBJECTID <= idTo
      );
      // DeepCopy der Ursprungsdatei
      filteredShape = JSON.parse(JSON.stringify(filtered));
      // TODO Marc prüfen, welches winding der Geometrie vorliegt, damit das reverse & DeepCopy erspart bleibt
      // Momentan Umkehren des Koordinaten Arrays, da d3 die Koordinaten im Uhrzeigersinn erwartet, was
      // konträr zum RFC 7946 steht
      filteredShape.forEach((elem) => elem.geometry.coordinates[0].reverse());
    };

    /**
     * Anhand des geopath werden aus Koordinaten svg Zeichenbefehle generiert
     */
    const buildGeoPath = () => {
      // Projektion anhand von ganz Bayern - Bayern gesamt muss an Index 10 liegen
      // Für Minimap ist hier statisch Index 10 Interessant, da nicht an Features "gezoomt" werden soll
      const bayernShapeReversed = JSON.parse(
        JSON.stringify(this.bayernShape.features[10])
      );
      bayernShapeReversed.geometry.coordinates[0].reverse();
      const projection = geoConicConformal()
        .parallels([35, 65])
        .rotate([-10, 0])
        .fitSize(
          [svgWidth, svgHeight - svgPaddingTop - svgPaddingBottom],
          bayernShapeReversed
        );

      this.geopath = geoPath(projection);
    };

    /**
     * @returns DOM-Node von SVG
     */
    const createSVG = () => {
      const svgNode = this.minimapSvgRef.current;
      const svgSelection = select(svgNode);

      if (!svgNode.querySelector('defs')) {
        svgSelection.attr('viewBox', `0 0 ${svgWidth} ${svgHeight}`);

        const defs = svgSelection.append('defs');

        defs
          .append('path')
          .attr('class', 'outline')
          .attr('d', this.geopath({ type: 'Sphere' }));

        defs
          .append('clipPath')
          .attr('class', 'clip')
          .append('use')
          .attr('href', new URL('#outline', window.location));

        svgSelection.append('g');
      } else {
        // Entferne alte Regionen aus der Minimap
        svgSelection.selectAll('path').remove();
      }
      return svgSelection;
    };

    const drawMinimap = () => {
      const svg = createSVG();

      // Das Layer des SVGs, auf dem die Regionen gezeichnet werden sollen
      const regionsLayer = svg.select('g');

      regionsLayer
        .selectAll('path')
        .data(filteredShape)
        .enter()
        .append('path')
        .attr('d', this.geopath)
        .attr('dataname', (d) => d.properties.Name)
        .attr('dataid', (d) => d.properties.OBJECTID)
        .attr('id', (d) => d.properties.OBJECTID)
        .attr('transform', `translate(0, ${svgPaddingTop})`)
        .on('click', (event) => {
          const regionName = event.target.attributes.dataname.value;

          this.selectRegionOnMap(event.target, regionName);

          apiQuery[4] = parseInt(event.target.attributes.dataid.value, 10);
          activeRegionBuffer = parseInt(
            event.target.attributes.dataid.value,
            10
          );

          this.setState({
            regionName,
          });
        })
        .on('mouseover', (event) => {
          select(event.target).style('fill', colors.themeBase.accentDark);
        })
        .on('mouseout', (event) => {
          select(event.target).style('fill', colors.themeBase.box);
          select('path.regionActive').style(
            'fill',
            colors.themeBase.accentDark
          );
        })
        .style('fill', colors.themeBase.box)
        .style('stroke', colors.themeBase.accentDark)
        .style('stroke-linejoin', 'round')
        .style('stroke-width', '1px');

      const { activeRegion, regionName } = this.state;

      if (activeRegion) {
        this.selectRegionOnMap(
          document.querySelector(`path[dataid="${activeRegion}"]`),
          regionName
        );
      }
    };

    /**
     * Filtern der Regionen und neu zeichnen der Karte
     */
    const redraw = () => {
      filterShapes();
      drawMinimap();
    };

    // Bayern Shape als Singleton - kein mehrfaches laden der JSON
    if (!this.bayernShape) {
      json(shapeURL).then((shape) => {
        this.bayernShape = shape;
        this.setState({
          fullShape: this.bayernShape,
          filteredShape: [this.bayernShape.features[10]],
        });
        // Initiales bauen des geopath
        buildGeoPath();
        redraw();
      });
    } else {
      redraw();
    }
  };

  buildSlider = (object, epoch) => {
    const { selectedYear } = this.state;
    if (this.slider || !object) {
      return;
    }

    let startEndYears;
    let step;
    let range;

    const parsedYear = parseInt(selectedYear, 10);
    if (epoch === 'future') {
      startEndYears = [parsedYear - 15, parsedYear + 15];
      step = 10;
      range = {
        min: 2020,
        max: 2100,
      };
    } else {
      startEndYears = [parsedYear, parsedYear + 1];
      step = 1;
      range = {
        min: 1951,
        max: 2020,
      };
    }
    this.slider = noUiSlider.create(object, {
      start: startEndYears,
      step,
      behaviour: 'drag-fixed',
      connect: true,
      range,
    });
    object.noUiSlider.on('update', (values) => {
      const startYears = Math.round(values[0]);
      const endYears = Math.round(values[1]);
      const timeStartEnd = `${startYears + 1} - ${endYears}`;
      this.timeframeBegin.current.innerHTML =
        epoch === 'future' ? timeStartEnd : `${startYears}`;

      this.setState({
        selectedYear: epoch === 'future' ? timeStartEnd : `${startYears}`,
      });
      const diffValue = epoch === 'future' ? startYears + 15 : startYears;
      this.buildQuery(diffValue, 3);
    });
  };

  buildQuery = (value, index) => {
    apiQuery[index] = value;
  };

  toggleRCP = (className, event) => {
    const rcpButtonActive = event.target.classList;
    const { rcpFilter } = this.state;

    rcpButtonActive.toggle('activeRCP');

    if (
      rcpButtonActive.contains('activeRCP') &&
      !rcpFilter.includes(className)
    ) {
      const filter = rcpFilter.concat(className);

      this.setState({
        rcpFilter: filter,
      });
    } else {
      const filter = rcpFilter.filter((item) => item !== className);

      if (rcpFilter.length > 1) {
        this.setState({
          rcpFilter: filter,
        });
      } else {
        rcpButtonActive.toggle('activeRCP');
      }
    }
  };

  removeOptions = (selectElement) => {
    for (let i = selectElement.length - 1; i >= 0; i -= 1) {
      selectElement.remove(i);
    }
  };

  initFilterSet = (contextArray) => {
    const bufferArray = [[], []];
    const { characteristics } = this.state;
    contextArray[0].forEach((element) => {
      bufferArray[0].push(characteristics[0][element]);
    });
    contextArray[1].forEach((element) => {
      bufferArray[1].push(characteristics[1][element]);
    });
    this.setState({ filteredOptions: bufferArray, defaultIsSet: true }, () => {
      const { epoch } = this.state;
      this.buildSlider(this.timeframe.current, epoch);
    });
  };

  filterCharacteristics = (switchFilter) => {
    dynamicContext = selectbox.climatics
      .find((el) => el.value === switchFilter)
      .label.toLowerCase();

    let index = 0;
    switch (switchFilter) {
      case 'pr':
        index = 1;
        break;

      case 'agrar':
        index = 2;
        break;

      case 'health':
        index = 3;
        break;

      case 'tourism':
        index = 4;
        break;

      case 'water':
        index = 5;
        break;

      case 'nature':
        index = 6;
        break;

      case 'forest':
        index = 7;
        break;

      case 'soil':
        index = 8;
        break;

      case 'catastrophe':
        index = 9;
        break;

      case 'planning':
        index = 10;
        break;

      case 'city':
        index = 11;
        break;

      case 'architecture':
        index = 12;
        break;

      case 'traffic':
        index = 13;
        break;

      case 'energy':
        index = 14;
        break;

      case 'industry':
        index = 15;
        break;

      case 'finance':
        index = 16;
        break;

      default:
        index = 0;
        break;
    }

    console.log(index);

    apiQuery[0] =
      switchFilter === 'pr' || switchFilter === 'water' ? 'pr-Y' : 'tas-TM-Y';

    const selectElement = document.getElementById('characteristics');
    this.removeOptions(selectElement);
    filterSet[0][index].forEach((element) => {
      const option = document.createElement('option');
      option.value = element.value;
      option.dataset.unit = element.unit;

      if (element.value === apiQuery[0]) {
        option.setAttribute('selected', 'selected');
        this.selectedCharacteristic.label = element.label;
        this.selectedCharacteristic.unit = element.unit;
      }
      option.innerHTML = element.label;
      selectElement.appendChild(option);
    });
  };

  setChartTitle = (mode) => {
    let chartTitle;
    let chartUnit;
    if (mode === 'time') {
      chartTitle = `Änderung ${timeCharacteristic.label}`;
      chartUnit = timeCharacteristic.unit;
    } else if (mode === 'scenarios') {
      chartTitle = `Änderung ${this.selectedCharacteristic.label}`;
      chartUnit = this.selectedCharacteristic.unit;
    } else {
      chartTitle = this.selectedCharacteristic.label;
      chartUnit = this.selectedCharacteristic.unit;
    }
    this.setState({
      chartTitle,
      unit: chartUnit,
    });
  };

  useFilter = (view = null) => {
    const apiQueryNew = [...apiQuery];
    const {
      modeFinal,
      vistype,
      rcpFilter,
      regionPartitioningValue,
      regionName,
      regionPartitioning,
      calledRCP,
      regionNameFinal,
    } = this.state;

    this.setChartTitle(modeFinal);

    this.setState({
      vistype: view || vistype,
      apiParameter: apiQueryNew,
      activeRegion: activeRegionBuffer,
      regionPartitioningValueFinal: regionPartitioningValue,
      regionPartitioningLabelFinal: regionPartitioning,
      regionNameFinal: regionName !== '' ? regionName : regionNameFinal,
      rcpFilter,
    });

    if (vistype === 'map') {
      this.setState({
        rcpFilter: [`${calledRCP}_graph`],
      });
    }
  };

  toggleView = () => {
    const { vistype } = this.state;
    this.useFilter(vistype === 'map' ? 'chart' : 'map');
  };

  render() {
    const {
      vistype,
      mode,
      modeFinal,
      filteredOptions,
      fullShape,
      filteredShape,
      activeRegion,
      regionName,
      regionNameFinal,
      regionPartitioning,
      regionPartitioningValueFinal,
      regionPartitioningLabelFinal,
      calledRCP,
      calledComparisonRCP,
      rcpFilter,
      defaultClimatics,
      defaultCharacteristics,
      chartTitle,
      unit,
      selectedYear,
      apiParameter,
      aggregation,
      epoch,
      yearsMapSelection,
      usp,
      context,
    } = this.state;
    const { apiEndpoint } = this.props;
    let rcpUI = null;
    let timeframe = null;
    let climateParameters = null;
    let minMedMax = '';
    let optionsArray = [];
    let rcp26Graph = null;
    let rcp45Graph = null;
    let rcp85Graph = null;

    const getConfigString = () => {
      const yearsMapArray = apiQuery[5].map((element, index) => [
        `param[yearsMapSelection][${index}]`,
        element.value,
      ]);
      const yearsMapString = new URLSearchParams(yearsMapArray);
      const rcpFilterArray = rcpFilter.map((element, index) => [
        `param[rcpFilter][${index}]`,
        element,
      ]);

      const contextParam = dynamicContext || context;
      const rcpFilterString = new URLSearchParams(rcpFilterArray);
      return `param[epoch]=${epoch}&param[vistype]=${vistype}&param[context]=${contextParam}&param[mode]=${modeFinal}&param[regionPartitioning]=${regionPartitioningLabelFinal}&param[regionPartValue]=${regionPartitioningValueFinal}&param[region]=${apiParameter[4]}&param[regionName]=${regionNameFinal}&param[climatics]=${defaultClimatics}&param[characteristics]=${apiParameter[0]}&param[timeCharacteristic]=${timeCharacteristic.value}&param[aggregation]=${apiParameter[2]}&param[year]=${apiParameter[3]}&${yearsMapString}&${rcpFilterString}&param[comp_rcp]=${calledComparisonRCP}&param[rcp]=${apiParameter[1]}`;
    };

    if (rcpFilter.length > 0) {
      rcp26Graph = rcpFilter.find((element) => element === 'rcp26_graph');
      rcp45Graph = rcpFilter.find((element) => element === 'rcp45_graph');
      rcp85Graph = rcpFilter.find((element) => element === 'rcp85_graph');
    }
    if (context === 'niederschlag' || context === 'wasserwirtschaft') {
      [, optionsArray] = filteredOptions;
    } else {
      [optionsArray] = filteredOptions;
    }
    if (epoch === 'future') {
      if (vistype === 'map') {
        if (modeFinal === 'standard' || modeFinal === 'time') {
          // Climate of the future and type map
          rcpUI = (
            <label htmlFor="rcp">
              {config.filters.climatescenarios.title}
              <Tippy
                content={config.filters.climatescenarios.description}
                interactive
              >
                <span className="icon">
                  <Icon.Info className="feather" />
                </span>
              </Tippy>
              <select
                name="rcp"
                ref={this.rcp}
                defaultValue="rcp85"
                onBlur={(e) => {
                  this.buildQuery(e.currentTarget.value, 1);
                  this.setState({ calledRCP: e.currentTarget.value });
                }}
              >
                {selectbox.rcp.map((option) => {
                  return (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  );
                })}
              </select>
            </label>
          );
        } else if (modeFinal === 'scenarios') {
          rcpUI = (
            <label htmlFor="rcpComparison">
              {config.filters.climatescenarios.title}
              <Tippy
                content={config.filters.climatescenarios.description}
                interactive
              >
                <span className="icon">
                  <Icon.Info className="feather" />
                </span>
              </Tippy>
              <select
                name="rcpComparison"
                ref={this.rcpComparison}
                defaultValue="rcp85VSrcp26"
                onBlur={(e) => {
                  this.setState({ calledComparisonRCP: e.currentTarget.value });
                }}
              >
                {selectbox.rcpComparison.map((option) => {
                  return (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  );
                })}
              </select>
            </label>
          );
        }

        minMedMax = (
          <label htmlFor="klimalelemente">
            {config.filters.values.title}
            <Tippy content={config.filters.values.description} interactive>
              <span className="icon">
                <Icon.Info className="feather" />
              </span>
            </Tippy>
            <select
              name="klimaelemente"
              ref={this.minMaxMed}
              defaultValue={aggregation}
              onBlur={(e) => this.buildQuery(e.currentTarget.value, 2)}
            >
              {selectbox.minMax.map((option) => {
                return (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                );
              })}
            </select>
          </label>
        );
      } else {
        // Climate of the future and type chart
        rcpUI = (
          <label htmlFor="rcp">
            {config.filters.climatescenarios.title}
            <Tippy
              content={config.filters.climatescenarios.description}
              interactive
            >
              <span className="icon">
                <Icon.Info className="feather" />
              </span>
            </Tippy>
            <span className="rcpWrapper">
              <button
                name="rcp_2_6"
                value="_rcp_2_6"
                type="submit"
                className={`rcpButton${rcp26Graph ? ' activeRCP' : ''} `}
                onClick={(event) => this.toggleRCP('rcp26_graph', event)}
              >
                RCP 2.6
              </button>
              <button
                name="rcp_4_5"
                value="_rcp_4_5"
                type="submit"
                className={`rcpButton${rcp45Graph ? ' activeRCP' : ''} `}
                onClick={(event) => this.toggleRCP('rcp45_graph', event)}
              >
                RCP 4.5
              </button>
              <button
                name="rcp_8_5"
                value="_rcp_8_5"
                type="submit"
                className={`rcpButton${rcp85Graph ? ' activeRCP' : ''} `}
                onClick={(event) => this.toggleRCP('rcp85_graph', event)}
              >
                RCP 8.5
              </button>
            </span>
          </label>
        );
      }
    }

    if (modeFinal !== 'warmingstripes') {
      const textPeriod =
        epoch === 'future'
          ? config.filters.period.title
          : config.filters.time.title;
      const textInfo =
        epoch === 'future'
          ? config.filters.period.description
          : config.filters.time.description;
      if (modeFinal !== 'years') {
        timeframe = (
          <label
            htmlFor="refPeriod"
            className={`timePeriod ${
              vistype === 'map' || (modeFinal === 'time' && epoch === 'future')
                ? 'active'
                : ''
            }`}
          >
            {textPeriod}
            <Tippy content={textInfo} interactive>
              <span className="icon">
                <Icon.Info className="feather" />
              </span>
            </Tippy>
            <div ref={this.timeframe} />
            <span ref={this.timeframeBegin} className="timeframeOutput" />
          </label>
        );
      }

      const mapYearSelectCallback = (values) => {
        apiQuery[5] = values;
      };

      climateParameters = (optionsArray) => {
        return (
          <>
            <div style={modeFinal === 'time' ? { display: 'none' } : {}}>
              <label
                htmlFor="climatics"
                style={modeFinal === 'time' ? { display: 'none' } : {}}
              >
                {config.filters.themes.title}
                <Tippy content={config.filters.themes.description} interactive>
                  <span className="icon">
                    <Icon.Info className="feather" />
                  </span>
                </Tippy>
                <select
                  name="climatics"
                  ref={this.climatics}
                  defaultValue={defaultClimatics}
                  onChange={(e) => {
                    this.filterCharacteristics(e.currentTarget.value);
                  }}
                >
                  <optgroup label="Klima">
                    {selectbox.climatics.slice(0, 2).map((option) => {
                      return (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      );
                    })}
                  </optgroup>
                  <optgroup label="Handlungsfelder">
                    {selectbox.climatics.slice(2).map((option) => {
                      return (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      );
                    })}
                  </optgroup>
                </select>
              </label>
              <label
                htmlFor="characteristics"
                style={modeFinal === 'time' ? { display: 'none' } : {}}
              >
                {config.filters.climatevalues.title}
                <Tippy
                  content={parse(config.filters.climatevalues.description)}
                  interactive
                >
                  <span className="icon">
                    <Icon.Info className="feather" />
                  </span>
                </Tippy>
                <select
                  name="characteristics"
                  id="characteristics"
                  ref={this.characteristics}
                  defaultValue={defaultCharacteristics}
                  onBlur={(e) => {
                    this.buildQuery(e.currentTarget.value, 0);
                    const { characteristics } = this.state;
                    const selectedCharacteristicTemp = characteristics
                      .flat()
                      .find((element) => {
                        return element.value === apiQuery[0];
                      });
                    this.selectedCharacteristic = selectedCharacteristicTemp;

                    // const selectedEL =
                    //   e.currentTarget.options[e.currentTarget.selectedIndex];
                    // this.selectedCharacteristic.label = selectedEL.text;
                    // this.selectedCharacteristic.unit = selectedEL.dataset.unit;
                  }}
                >
                  {optionsArray.map((option) => {
                    return (
                      <option
                        key={option.value}
                        value={option.value}
                        data-unit={option.unit}
                      >
                        {option.label}
                      </option>
                    );
                  })}
                </select>
              </label>
            </div>
            <label
              htmlFor="time-characteristics"
              style={modeFinal !== 'time' ? { display: 'none' } : {}}
            >
              {config.filters.climatevalues.title}
              <Tippy
                content={parse(config.filters.climatevalues.description)}
                interactive
              >
                <span className="icon">
                  <Icon.Info className="feather" />
                </span>
              </Tippy>
              <select
                name="time-characteristics"
                id="time-characteristics"
                defaultValue={timeCharacteristics[0]}
                onBlur={(e) => {
                  timeCharacteristic = timeCharacteristics.find(
                    (element) => element.value === e.currentTarget.value
                  );
                  // timeCharacteristic = e.currentTarget.value;
                  if (timeCharacteristic.value.split('_')[0] === 'pr') {
                    apiQuery[0] = 'pr-Y';
                  } else {
                    apiQuery[0] = 'tas-TM-Y';
                  }
                }}
              >
                {timeCharacteristics.map((option) => {
                  return (
                    <option
                      key={option.value}
                      value={option.value}
                      data-unit={option.unit}
                    >
                      {option.label}
                    </option>
                  );
                })}
              </select>
            </label>
            {modeFinal === 'years' && (
              <MapYearSelect mapYearCallback={mapYearSelectCallback} />
            )}
          </>
        );
      };
    }

    if (filteredOptions.length) {
      return (
        <div className={`grid -gutter${usp === 'pdf' ? ' pdf-container' : ''}`}>
          <div className="filter grid__bit -w3-tablet">
            <h3>Filter</h3>
            <div>
              <label htmlFor="mode">
                {config.filters.mode.title}
                <Tippy content={config.filters.mode.description} interactive>
                  <span className="icon">
                    <Icon.Info className="feather" />
                  </span>
                </Tippy>
                <select name="mode" ref={this.mode} onChange={this.changeMode}>
                  {selectbox.modes[epoch].map((option) => {
                    return (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    );
                  })}
                </select>
              </label>
              <button
                type="button"
                className="useFilter"
                onClick={() => this.applyMode()}
              >
                <Icon.Filter className="feather" />
                Modus anwenden
              </button>
            </div>

            <label htmlFor="regions">
              {config.filters.region.title}
              <Tippy content={config.filters.region.description} interactive>
                <span className="icon">
                  <Icon.Info className="feather" />
                </span>
              </Tippy>
              <select
                name="regions"
                ref={this.regions}
                onChange={this.changeRegion}
              >
                {selectbox.regions.map((option) => {
                  return (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  );
                })}
              </select>
            </label>
            <div className="minimap-container">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="minimapShape"
                id="minimapShape"
                ref={this.minimapSvgRef}
              />
              <label htmlFor="minimapShape" className="nameRegion">
                {regionName}
              </label>
            </div>
            {climateParameters && climateParameters(optionsArray)}
            {rcpUI}
            {minMedMax}
            {timeframe}
            <button
              type="button"
              className="useFilter"
              onClick={() => this.useFilter()}
              disabled={!regionName}
            >
              <Icon.Filter className="feather" />
              Filter anwenden
            </button>
          </div>
          <Datawrapper
            apiEndpoint={apiEndpoint}
            calledVariable={apiQuery[0]}
            vistype={vistype}
            mode={modeFinal}
            calledRCP={calledRCP}
            calledComparisonRCP={calledComparisonRCP}
            fullShape={fullShape}
            filteredShape={filteredShape}
            activeRegion={activeRegion}
            regionName={regionNameFinal}
            regionPartitioning={regionPartitioningLabelFinal}
            chartTitle={chartTitle}
            unit={unit}
            apiParameter={apiParameter}
            selectedYear={selectedYear}
            epoch={epoch}
            rcp={{
              viewIsActive: !!rcpUI,
              selectedItems: rcpUI ? [...rcpFilter] : [],
            }}
            timeCharacteristic={timeCharacteristic.value}
            configString={getConfigString()}
            usp={usp}
          >
            {(disabled = false) => (
              <div className="toggleVistype-container">
                <button
                  type="button"
                  className="data-download"
                  style={
                    modeFinal === 'warmingstripes' ? { display: 'none' } : {}
                  }
                  onClick={() => {
                    window.location.assign(
                      `${
                        window.location.origin
                      }/grundlagen/datendownload?epoch=${epoch}&regionPartitioning=${regionPartitioningLabelFinal}&regionPartValue=${regionPartitioningValueFinal}&region=${activeRegion}&regionName=${regionNameFinal}&format=${
                        vistype === 'chart' ? 'csv' : 'nc'
                      }`
                    );
                  }}
                  disabled={!regionName}
                >
                  <Icon.ArrowRight className="feather" />
                  Datendownload
                </button>
                <IconButton
                  classNames={`${
                    vistype === 'map' ? 'buttonChart' : 'buttonMap'
                  }`}
                  text={vistype === 'map' ? 'Zeitverlauf' : 'Kartendarstellung'}
                  icon={vistype === 'map' ? 'trendingUp' : 'globe'}
                  disabled={disabled}
                  onClick={this.toggleView}
                  style={
                    modeFinal === 'warmingstripes' || modeFinal === 'years'
                      ? { display: 'none' }
                      : {}
                  }
                />
              </div>
            )}
          </Datawrapper>
        </div>
      );
    }
    return <Icon.Loader className="feather" />;
  }
}

QueryBuilder.propTypes = {
  apiEndpoint: PropTypes.string,
  context: PropTypes.string,
  epoch: PropTypes.string,
  vistype: PropTypes.string,
};

QueryBuilder.defaultProps = {
  apiEndpoint: '',
  context: '',
  epoch: '',
  vistype: '',
};

export default QueryBuilder;
